import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export enum PopupType {
  INFO = "info",
  SUCCESS = "success",
  WARNING = "warning",
  ERROR = "error",
  CONFIRM = "confirm",
  CUSTOM = "custom",
}

export interface PopupButton {
  text: string;
  onClick?: () => void;
  type?: "primary" | "default" | "dashed" | "text" | "link" | "danger";
}

export interface PopupConfig {
  id?: string;
  type: PopupType;
  title: string;
  message: string;
  buttons?: PopupButton[];
  onClose?: () => void;
  onConfirm?: () => void;
  onCancel?: () => void;
  closable?: boolean;
  maskClosable?: boolean;
  width?: number | string;
  zIndex?: number;
  priority?: number; // Higher number = higher priority
  autoClose?: number; // Auto close after milliseconds
  data?: any; // Custom data
}

interface PopupState {
  popups: PopupConfig[];
  currentPopup: PopupConfig | null;
}

const initialState: PopupState = {
  popups: [],
  currentPopup: null,
};

const popupSlice = createSlice({
  name: "popup",
  initialState,
  reducers: {
    showPopup: (state, action: PayloadAction<PopupConfig>) => {
      const popup: PopupConfig = {
        id: action.payload.id || `popup-${Date.now()}-${Math.random()}`,
        closable: true,
        maskClosable: true,
        priority: 0,
        ...action.payload,
      };

      // Add to queue
      state.popups.push(popup);

      // Sort by priority (higher first)
      state.popups.sort((a, b) => (b.priority || 0) - (a.priority || 0));

      // Show the highest priority popup
      state.currentPopup = state.popups[0] || null;
    },
    closePopup: (state, action: PayloadAction<string | undefined>) => {
      const popupId = action.payload;
      
      if (popupId) {
        // Close specific popup
        state.popups = state.popups.filter((p) => p.id !== popupId);
      } else {
        // Close current popup
        if (state.currentPopup) {
          state.popups = state.popups.filter(
            (p) => p.id !== state.currentPopup?.id
          );
        }
      }

      // Show next popup in queue
      state.currentPopup = state.popups[0] || null;
    },
    closeAllPopups: (state) => {
      state.popups = [];
      state.currentPopup = null;
    },
    updatePopup: (
      state,
      action: PayloadAction<{ id: string; config: Partial<PopupConfig> }>
    ) => {
      const { id, config } = action.payload;
      const index = state.popups.findIndex((p) => p.id === id);
      
      if (index !== -1) {
        state.popups[index] = { ...state.popups[index], ...config };
        
        // Re-sort if priority changed
        if (config.priority !== undefined) {
          state.popups.sort((a, b) => (b.priority || 0) - (a.priority || 0));
        }
        
        // Update current if it's the one being updated
        if (state.currentPopup?.id === id) {
          state.currentPopup = state.popups[index];
        } else {
          // Update current to highest priority
          state.currentPopup = state.popups[0] || null;
        }
      }
    },
  },
});

export const { showPopup, closePopup, closeAllPopups, updatePopup } =
  popupSlice.actions;
export default popupSlice.reducer;
